import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { RegionalPremierAds } from '../components/RegionalPremierAds';

export const FindAuctioneer = () => {
  const [zipCode, setZipCode] = useState('');
  const [radius, setRadius] = useState(50);
  const [specialty, setSpecialty] = useState('');
  const [certification, setCertification] = useState('');
  const [auctioneers, setAuctioneers] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedAuctioneer, setSelectedAuctioneer] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  useEffect(() => {
    fetchSpecialties();
    fetchCertifications();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auctioneers/specialties/list`);
      const data = await response.json();
      setSpecialties(data.specialties);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };

  const fetchCertifications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auctioneers/certifications/list`);
      const data = await response.json();
      setCertifications(data.certifications);
    } catch (error) {
      console.error('Error fetching certifications:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);

    try {
      const params = new URLSearchParams();
      if (zipCode) params.append('zip_code', zipCode);
      params.append('radius_miles', radius);
      if (specialty) params.append('specialty', specialty);
      if (certification) params.append('certification', certification);
      params.append('limit', 100);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auctioneers/search?${params.toString()}`
      );
      const data = await response.json();
      setAuctioneers(data.auctioneers);
    } catch (error) {
      console.error('Error searching auctioneers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestQuote = (auctioneer) => {
    setSelectedAuctioneer(auctioneer);
    setShowQuoteModal(true);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Find a Licensed Auctioneer
              </h1>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                Connect with professional, licensed auctioneers for your estate sale, farm equipment, real estate, or business liquidation needs
              </p>
            </div>
          </div>
        </div>

        {/* Search Form */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="bg-white rounded-lg shadow-xl p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="Enter ZIP"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Radius
                  </label>
                  <select
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value={25}>25 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                    <option value={200}>200 miles</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialty
                  </label>
                  <select
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">All Specialties</option>
                    {specialties.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certification
                  </label>
                  <select
                    value={certification}
                    onChange={(e) => setCertification(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Any</option>
                    {certifications.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition disabled:opacity-50"
                >
                  {loading ? 'Searching...' : '🔍 Search Auctioneers'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Results */}
            <div className="lg:col-span-3">
              {searched && !loading && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {auctioneers.length} Auctioneer{auctioneers.length !== 1 ? 's' : ''} Found
                  </h2>
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Searching for auctioneers...</p>
                </div>
              ) : auctioneers.length === 0 && searched ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <span className="text-6xl mb-4 block">🔍</span>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Auctioneers Found</h3>
                  <p className="text-gray-600">Try expanding your search radius or removing filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {auctioneers.map((auctioneer, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border-2 ${
                    auctioneer.is_premier ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-white' : 'border-gray-200'
                  }`}
                >
                  {/* Premier Banner */}
                  {auctioneer.is_premier && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 -mx-6 -mt-6 mb-4 px-6 py-2 rounded-t-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-bold text-sm flex items-center">
                          🏆 PREMIER LISTING
                        </span>
                        <span className="bg-white text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                          TOP RATED
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Header with Logo */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start flex-1">
                      {auctioneer.logo_url && (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${auctioneer.logo_url}`}
                          alt={`${auctioneer.name} logo`}
                          className="w-16 h-16 rounded-lg object-cover mr-4 border-2 border-gray-200"
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{auctioneer.name}</h3>
                        {auctioneer.marketing_tagline && auctioneer.is_premier && (
                          <p className="text-sm text-gray-700 italic mb-2">"{auctioneer.marketing_tagline}"</p>
                        )}
                        <p className="text-sm text-gray-600">
                          📍 {auctioneer.city}, {auctioneer.state}
                          {auctioneer.distance_miles && (
                            <span className="ml-2 text-green-600 font-medium">
                              ({auctioneer.distance_miles} miles away)
                            </span>
                          )}
                        </p>
                        {auctioneer.license_number && (
                          <p className="text-xs text-gray-500 mt-1">
                            License: {auctioneer.license_number} ({auctioneer.license_state})
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {auctioneer.is_premier && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
                          🏆 PREMIER
                        </span>
                      )}
                      {auctioneer.verified && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ Verified
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Reviews */}
                  {(auctioneer.google_rating || auctioneer.yelp_rating || auctioneer.manual_rating) && (
                    <div className="mb-3 flex items-center gap-4">
                      {auctioneer.google_rating && (
                        <div className="flex items-center">
                          <span className="text-yellow-500 mr-1">⭐</span>
                          <span className="font-semibold">{auctioneer.google_rating}</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({auctioneer.google_review_count} Google reviews)
                          </span>
                        </div>
                      )}
                      {auctioneer.yelp_rating && (
                        <div className="flex items-center">
                          <span className="text-red-500 mr-1">⭐</span>
                          <span className="font-semibold">{auctioneer.yelp_rating}</span>
                          <span className="text-xs text-gray-500 ml-1">
                            ({auctioneer.yelp_review_count} Yelp reviews)
                          </span>
                        </div>
                      )}
                      {auctioneer.manual_rating && !auctioneer.google_rating && !auctioneer.yelp_rating && (
                        <div className="flex items-center">
                          <span className="text-yellow-500 mr-1">⭐</span>
                          <span className="font-semibold">{auctioneer.manual_rating}</span>
                          {auctioneer.manual_review_count && (
                            <span className="text-xs text-gray-500 ml-1">
                              ({auctioneer.manual_review_count} reviews)
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Preview */}
                  {auctioneer.is_premier && (auctioneer.video_url || auctioneer.video_file_url) && (
                    <div className="mb-4">
                      {auctioneer.video_url && auctioneer.video_url.includes('youtube') && (
                        <iframe
                          width="100%"
                          height="200"
                          src={auctioneer.video_url.replace('watch?v=', 'embed/')}
                          title="Auctioneer video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="rounded-lg"
                        ></iframe>
                      )}
                      {auctioneer.video_file_url && (
                        <video
                          controls
                          className="w-full rounded-lg"
                          style={{maxHeight: '200px'}}
                        >
                          <source src={`${process.env.REACT_APP_BACKEND_URL}${auctioneer.video_file_url}`} type="video/mp4" />
                        </video>
                      )}
                    </div>
                  )}

                  {/* Specialties */}
                  {auctioneer.specialties && auctioneer.specialties.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Specialties:</p>
                      <div className="flex flex-wrap gap-1">
                        {auctioneer.specialties.slice(0, 3).map((spec, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                          >
                            {spec}
                          </span>
                        ))}
                        {auctioneer.specialties.length > 3 && (
                          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            +{auctioneer.specialties.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Certifications */}
                  {auctioneer.certifications && auctioneer.certifications.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Certifications:</p>
                      <div className="flex flex-wrap gap-1">
                        {auctioneer.certifications.map((cert, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded font-medium"
                          >
                            {cert.split(' - ')[0]}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {auctioneer.years_experience && (
                    <p className="text-sm text-gray-600 mb-3">
                      💼 {auctioneer.years_experience}+ years experience
                    </p>
                  )}

                  {/* Bio */}
                  {auctioneer.bio && (
                    <p className="text-sm text-gray-700 mb-4 line-clamp-2">{auctioneer.bio}</p>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    {!auctioneer.claimed && (
                      <a
                        href={`/auctioneer/individual/claim?id=${auctioneer.id}`}
                        className="block w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition text-center"
                      >
                        🎯 Is this you? Claim this listing
                      </a>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRequestQuote(auctioneer)}
                        className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition"
                      >
                        📧 Request Quote
                      </button>
                      {auctioneer.phone && (
                        <a
                          href={`tel:${auctioneer.phone}`}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition"
                        >
                          📞
                        </a>
                      )}
                      {auctioneer.website && (
                        <a
                          href={auctioneer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition"
                        >
                          🌐
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
                </div>
              )}
            </div>

            {/* Sidebar - Regional Premier Ads */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <RegionalPremierAds />
              </div>
            </div>
          </div>
        </div>

        {/* Quote Request Modal */}
        {showQuoteModal && selectedAuctioneer && (
          <QuoteModal
            auctioneer={selectedAuctioneer}
            onClose={() => {
              setShowQuoteModal(false);
              setSelectedAuctioneer(null);
            }}
          />
        )}

        {/* Request Listing CTA */}
        <section className="bg-gradient-to-r from-amber-500 to-orange-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-white text-center md:text-left">
                <h3 className="text-3xl font-bold mb-2">
                  Don't See Your Name Listed?
                </h3>
                <p className="text-lg">
                  Join thousands of licensed auctioneers in our directory. Get discovered by clients nationwide!
                </p>
              </div>
              <a
                href="/auctioneer/request-listing"
                className="px-8 py-4 bg-white hover:bg-gray-100 text-amber-600 font-bold rounded-lg transition-colors shadow-lg flex items-center whitespace-nowrap"
              >
                📝 Request Listing
              </a>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

// Quote Request Modal Component
const QuoteModal = ({ auctioneer, onClose }) => {
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    saleType: '',
    location: '',
    preferredDate: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auctioneers/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auctioneer_id: auctioneer.id,
          client_name: formData.clientName,
          client_email: formData.clientEmail,
          client_phone: formData.clientPhone,
          sale_type: formData.saleType,
          location: formData.location,
          preferred_date: formData.preferredDate,
          description: formData.description
        }),
      });

      if (!response.ok) throw new Error('Failed to send quote request');

      setSuccess(true);
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Request a Quote</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
            <p className="text-sm text-green-900">
              <strong>{auctioneer.name}</strong><br />
              {auctioneer.city}, {auctioneer.state}
            </p>
          </div>

          {success ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quote Request Sent!</h3>
              <p className="text-gray-600">
                {auctioneer.name} will contact you shortly to discuss your needs.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.clientEmail}
                    onChange={(e) => setFormData({...formData, clientEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sale Type *</label>
                  <select
                    required
                    value={formData.saleType}
                    onChange={(e) => setFormData({...formData, saleType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="">Select type...</option>
                    <option value="estate">Estate Sale</option>
                    <option value="farm">Farm Equipment</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="business">Business Liquidation</option>
                    <option value="vehicles">Vehicles</option>
                    <option value="personal">Personal Property</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="City, State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                  <input
                    type="date"
                    value={formData.preferredDate}
                    onChange={(e) => setFormData({...formData, preferredDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe what you need auctioned, estimated value, timeframe, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Quote Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
